import React from 'react'

import swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive,
  faBox,
  faBroadcastTower,
  faCog,
  faDownload,
  faNewspaper,
  faPencil,
  faPencilAlt,
  faShareAlt,
  faStore,
  faThumbsDown,
  faThumbsUp,
  faTrashAlt,
  faTruck,
} from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'
import {
  Box, Grid, HoverHintWrapper, IconWithMenu, Link, LoadingBubbles, StatusBadge, Text,
} from '@domain-group/suit-theme'

import useLoginToRealhub from '@hooks/useLoginToRealhub'
import useTemplatePage from '@hooks/useTemplatePage'

import handleCallbackAction from '@functions/handleCallbackAction'
import { loadBrandTestingLink } from '@functions/templatePageUtilities'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { TemplatePageModel } from '@models/templatePage'

import AuthenticatedDownload from '@components/AuthenticatedDownload'
import PagePreview from '@components/PagePreview'

import PermissionIcon from './PermissionIcon'

const confirmDelete = (deleteFn: () => void) => {
  const deleteTemplatePagePayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: deleteFn,
    },
    toastText: 'Template Archived',
  }

  swal
    .fire({
      title: 'Archive Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(deleteTemplatePagePayload)
      }
    })
}

const confirmToggleProductionReady = (productionReady: boolean | undefined, toggleProductionReadyFn: () => void) => {
  const toggleProductionReadyPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: toggleProductionReadyFn,
    },
    toastText: productionReady ? 'Template Unapproved' : 'Template Approved',
  }

  swal
    .fire({
      title: productionReady ? 'Unapprove Template?' : 'Approve Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${productionReady ? 'unnaprove' : 'approve'} it.`,
      confirmButtonColor: productionReady ? '#DD6B55' : '#43ac6a',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(toggleProductionReadyPayload)
      }
    })
}

const renderStatusBadge = (templatePage: TemplatePageModel) => {
  const { hidden, production_ready } = templatePage

  const boxProps = {
    left: 'medium',
    position: 'absolute',
    top: 'medium',
  }

  if (hidden){
    return (
      <StatusBadge
        boxProps={boxProps}
        color="red"
        // @ts-expect-error TS2322
        text={(
          <Box as="span">
            <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: 4 }} /> Archived
          </Box>
        )}
      />
    )
  }

  if (production_ready){
    return (
      <StatusBadge
        boxProps={boxProps}
        color="blue"
        // @ts-expect-error TS2322
        text={(
          <Box as="span">
            <FontAwesomeIcon icon={faBroadcastTower} style={{ marginRight: 4 }} /> Live
          </Box>
        )}
      />
    )
  }

  return (
    <StatusBadge
      boxProps={boxProps}
      color="lightGrey"
      // @ts-expect-error TS2322
      text={(
        <Box as="span">
          <FontAwesomeIcon icon={faPencil} style={{ marginRight: 4 }} /> Draft
        </Box>
      )}
    />
  )
}

// @ts-expect-error TS7006
const selectTemplatePage = (selectFn, options, setState) => {
  if (selectFn){
    setState({ loading: true })
    return selectFn(options)
  }

  return null
}

const goToArtwork = (e: React.MouseEvent, templatePage: TemplatePageModel) => {
  e.preventDefault()

  loadBrandTestingLink(templatePage).then((result) => {
    if (result.success && 'data' in result){
      const { hash } = result.data
      const artworkIdMatch = hash.match(/\/artworks\/(\d+)/)
      const artworkId = artworkIdMatch ? artworkIdMatch[1] : ''
      const url = `#/artworks${hash}?artworkId=${artworkId}&redirect=${encodeURIComponent(`#/templatePages?hidden=${templatePage?.hidden}&string=${templatePage.id}`)}`

      window.location.href = url
    }

    if (!result.success) toast.warning('Open link failed!')
  })
}

const defaultState = {
  loading: false,
}

interface TemplatePageGridTileProps {
  callbacks: {
    selectTemplatePage: () => void,
  },
  templatePage: TemplatePageModel,
}

const TemplatePageGridTile = (props: TemplatePageGridTileProps) => {
  const {
    callbacks: { selectTemplatePage: selectFn },
    templatePage: initTemplatePage,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { loading } = state

  const templatePagePayload = useTemplatePage(initTemplatePage)
  const {
    callbacks: {
      assignTemplatePagePermissions, deleteTemplatePage, toggleProductionReady,
    },
    urls: {
      templatePageDownloadPdfUrl,
    },
    createdByBrand,
    hasOriginTemplatePage,
    permissionCounts,
    templatePage,
    templatePage: {
      data, id, origin_template_id, preview_image, production_ready, title,
    },
  } = templatePagePayload

  const loginToRealhubPayload = useLoginToRealhub({
    redirect: { key: 'editTemplatePage', params: { templatePageId: id } },
  })

  const {
    callbacks: { loginToRealhub },
    isBrandUser,
  } = loginToRealhubPayload

  const shouldRenderEditTemplate = isBrandUser ? templatePage?.designer_type === 'Brand' : true

  const testingLink = data?.testing_link

  return (
    <Box flexDirection="column">
      <Box
        // onClick={() => selectTemplatePage(selectFn, { defaultCategoryId }, setState)}
        alignItems="center"
        backgroundColor="whiteGrey"
        border="1px solid"
        borderColor="lineColor"
        borderRadius={5}
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        height={190}
        justifyContent="center"
        position="relative"
      >
        {renderStatusBadge(templatePage)}

        {loading && (
          <Box
            alignItems="center"
            backgroundColor="rgba(255, 255, 255, 0.7)"
            bottom="0"
            display="flex"
            justifyContent="center"
            left="0"
            position="absolute"
            right="0"
            top="0"
          >
            <LoadingBubbles color="blue" />
          </Box>
        )}

        <PagePreview
          holderHeight={190}
          holderPadding={8}
          holderWidth={240}
          // @ts-expect-error
          imageHeight={templatePage?.dimensions?.pixels?.height}
          imageUrl={preview_image}
          // @ts-expect-error
          imageWidth={templatePage?.dimensions?.pixels?.width}
        />
      </Box>

      <Box
        border="1px solid"
        borderColor="lineColor"
        borderTop={0}
        borderBottomLeftRadius={5}
        borderBottomRightRadius={5}
        flexDirection="column"
      >
        <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" padding="large">
          <Box flexDirection="column" width="calc(100% - 16px)">
            <HoverHintWrapper
              boxProps={{ display: 'inline-block', position: 'relative', width: '100%' }}
              childrenHolderProps={{ color: 'bodyFontLightColor' }}
              hintComponent={<HoverHintWrapper.Hint>{title}</HoverHintWrapper.Hint>}
            >
              <Link textProps={{ color: 'bodyFontColor', fontSize: 'small', variant: 'ellipsis' }}>
                {hasOriginTemplatePage && <FontAwesomeIcon icon={faShareAlt} style={{ marginRight: 8 }} />}

                {title}
              </Link>
            </HoverHintWrapper>

            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small" variant="ellipsis">
              Template #{id} {createdByBrand && '(Brand Template)'}
            </Text>
          </Box>

          <IconWithMenu
            boxProps={{ marginLeft: 'medium' }}
            icon={(
              <Link greyLink>
                <FontAwesomeIcon icon={faCog} />
              </Link>
            )}
          >
            {/*
             // @ts-expect-error TS2741 */}
            <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
              <Link key="edit">
                <FontAwesomeIcon icon={faPencilAlt} /> Edit Title
              </Link>

              {!production_ready && shouldRenderEditTemplate && (
                <Link key="edit" onClick={() => loginToRealhub()}>
                  <FontAwesomeIcon icon={faPencilAlt} /> Edit Template Page
                </Link>
              )}

              {hasOriginTemplatePage && (
                <Link key="origin">
                  <FontAwesomeIcon icon={faShareAlt} /> Origin Details (#{origin_template_id})
                </Link>
              )}

              <Link key="archive" onClick={() => confirmDelete(deleteTemplatePage)}>
                <FontAwesomeIcon icon={faArchive} /> Archive
              </Link>

              <Link key="approve" onClick={() => confirmToggleProductionReady(production_ready, toggleProductionReady)}>
                <FontAwesomeIcon icon={production_ready ? faThumbsDown : faThumbsUp} />{' '}
                {production_ready ? 'Unapprove' : 'Approve for Production'}
              </Link>

              <AuthenticatedDownload key="download-template">
                <Link href={templatePageDownloadPdfUrl}>
                  <FontAwesomeIcon icon={faDownload} />{' '}
                  Download Template
                </Link>
              </AuthenticatedDownload>

              {testingLink && (
                // @ts-expect-error TS7006
                <Link key="testing-link" onClick={e => goToArtwork(e, templatePage)}>
                  <FontAwesomeIcon icon={faArchive} /> Testing Link
                </Link>
              )}
            </IconWithMenu.Menu>
          </IconWithMenu>
        </Box>

        {/*
         // @ts-expect-error TS2322 */}
        <Grid gridAutoRows="1fr" gridTemplateColumns="repeat(4, minmax(25%, 1fr))">
          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('providerIds') }}
            icon={<FontAwesomeIcon icon={faTruck} />}
            label="Providers"
            value={permissionCounts.providers}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('serviceProductIds') }}
            icon={<FontAwesomeIcon icon={faBox} />}
            label="Products"
            value={permissionCounts.products}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('publicationIds') }}
            icon={<FontAwesomeIcon icon={faNewspaper} />}
            label="Publications"
            value={permissionCounts.publications}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('agencyIds') }}
            icon={<FontAwesomeIcon icon={faStore} />}
            label="Agencies"
            value={permissionCounts.agencies}
          />
        </Grid>
      </Box>
    </Box>
  )
}

export default TemplatePageGridTile
